import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowUpRight, Truck, Zap, Shield, Clock } from "lucide-react";
import { StorySection } from "@/components/StorySection";
import { TestimonialsSection } from "@/components/TestimonialsSection";

const heroImg =
  "https://images.unsplash.com/photo-1485575301924-6891ef935dcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwc2VtaSUyMHRydWNrJTIwaGlnaHdheXxlbnwwfHx8fDE3ODA5MzYxMDF8MA&ixlib=rb-4.1.0&q=85";

const serviceIcons = {
  offert: Shield,
  express: Zap,
  lots: Truck,
  extra: Clock,
};

export default function Home() {
  const { t } = useLanguage();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/70 to-[#09090b]/40" />
        <div className="absolute inset-0 bg-black/30" />

        <div className="relative w-full px-6 md:px-12 lg:px-16 pb-16 md:pb-24 pt-32">
          <div className="fade-up">
            <div className="flex items-center gap-3 text-xs tracking-[0.35em] uppercase text-[#ff5a00] font-mono mb-6">
              <span className="w-8 h-px bg-[#ff5a00]" />
              {t.hero.eyebrow}
            </div>
            <h1 className="font-display text-6xl sm:text-8xl lg:text-[10rem] leading-[0.85] tracking-tight uppercase">
              {t.hero.title_l1}
              <span className="block text-[#ff5a00]">{t.hero.title_l2}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed">
              {t.hero.lead}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/quote"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center justify-between gap-4 bg-[#ff5a00] text-black px-7 py-4 text-sm tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors"
              >
                {t.hero.cta_primary}
                <ArrowUpRight
                  size={18}
                  className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                />
              </Link>
              <Link
                to="/team"
                data-testid="hero-cta-team"
                className="inline-flex items-center justify-between gap-4 border border-zinc-600 text-white px-7 py-4 text-sm tracking-[0.2em] uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
              >
                {t.hero.cta_secondary}
                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-zinc-800 bg-[#0d0d10] overflow-hidden">
        <div className="flex marquee-track py-5 whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 shrink-0">
              {[
                "EXPRESS",
                "LOTS",
                "FJÄRRTRANSPORT",
                "EXTRAKÖRNING",
                "SPECIALGODS",
                "24/7 BEREDSKAP",
                "SVERIGE",
              ].map((w, j) => (
                <span
                  key={j}
                  className="font-display text-3xl tracking-widest text-zinc-700 flex items-center gap-12"
                >
                  {w}
                  <span className="w-2 h-2 bg-[#ff5a00]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-3 border-y border-zinc-800">
          {t.hero.stats.map((s, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className={`p-10 md:p-12 border-zinc-800 ${
                i !== 0 ? "md:border-l" : ""
              } ${i !== t.hero.stats.length - 1 ? "border-b md:border-b-0" : ""}`}
            >
              <div className="font-display text-6xl md:text-7xl text-[#ff5a00] leading-none">
                {s.value}
              </div>
              <div className="mt-4 text-xs tracking-[0.3em] uppercase text-zinc-500 font-mono">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section
        data-testid="services-section"
        className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-zinc-800"
      >
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#ff5a00] font-mono mb-3">
              / 01 — {t.services.title}
            </div>
            <h2 className="font-display text-5xl md:text-7xl uppercase leading-none">
              {t.services.subtitle}
            </h2>
          </div>
          <Link
            to="/quote"
            data-testid="services-quote-cta"
            className="hidden md:inline-flex items-center gap-3 text-sm tracking-[0.2em] uppercase border-b border-[#ff5a00] text-white pb-1"
          >
            {t.common.cta_quote} <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border border-zinc-800">
          {t.services.items.map((item, i) => {
            const Icon = serviceIcons[item.key] || Truck;
            return (
              <Link
                to="/quote"
                key={item.key}
                data-testid={`service-card-${item.key}`}
                className={`group p-8 md:p-12 border-zinc-800 hover:bg-zinc-900 transition-colors ${
                  i % 2 === 1 ? "md:border-l" : ""
                } ${i < 2 ? "border-b" : ""} ${i === 2 ? "md:border-b-0 border-b" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 border border-zinc-700 flex items-center justify-center group-hover:border-[#ff5a00] group-hover:bg-[#ff5a00] transition-colors">
                    <Icon
                      size={22}
                      className="text-[#ff5a00] group-hover:text-black"
                    />
                  </div>
                  <span className="text-xs tracking-[0.3em] font-mono text-zinc-600 mt-2">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-8 font-display text-3xl md:text-4xl uppercase tracking-wide">
                  {item.title}
                </h3>
                <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">
                  {item.desc}
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#ff5a00] group-hover:gap-4 transition-all">
                  {t.common.cta_quote} <ArrowUpRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* STORY */}
      <StorySection />

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="relative overflow-hidden border border-zinc-800 bg-[#0d0d10] p-10 md:p-20">
          <div className="absolute -right-20 -top-20 w-60 h-60 bg-[#ff5a00]/10 blur-3xl" />
          <div className="relative">
            <h3 className="font-display text-4xl md:text-6xl uppercase max-w-3xl leading-none">
              {t.common.cta_quote.toUpperCase()}
            </h3>
            <p className="mt-4 text-zinc-400 max-w-xl">{t.quote.lead}</p>
            <Link
              to="/quote"
              data-testid="bottom-cta-quote"
              className="mt-8 inline-flex items-center gap-3 bg-[#ff5a00] text-black px-7 py-4 text-sm tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors"
            >
              {t.common.submit} <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
